import styled, { css } from 'styled-components'
import {
  StyledDropdownProps,
  StyledDropdownContentProps,
  StyledOptionProps,
  StyledSectionProps,
  StyledSectionHeaderProps,
} from './AdvancedDropdown.types'

const sizeStyles = {
  small: css`
    height: 32px;
    font-size: ${({ theme }) => theme.typography.fontSize.small};
    padding: 0 ${({ theme }) => theme.spacing.default};
  `,
  medium: css`
    height: 40px;
    font-size: ${({ theme }) => theme.typography.fontSize.body};
    padding: 0 ${({ theme }) => theme.spacing.medium};
  `,
  large: css`
    height: 48px;
    font-size: ${({ theme }) => theme.typography.fontSize.section};
    padding: 0 ${({ theme }) => theme.spacing.large};
  `,
}

export const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`

export const DropdownTrigger = styled.button<StyledDropdownProps>`
  width: ${({ $width }) => {
    if (typeof $width === 'number') return `${$width}px`
    if (typeof $width === 'string') return $width
    return '100%'
  }};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid
    ${({ theme, $error, $open }) => {
      if ($error) return theme.colors.border.error
      if ($open) return theme.colors.border.focus
      return theme.colors.border.default
    }};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all ${({ theme }) => theme.transitions.fast};
  text-align: left;

  ${({ $size }) => sizeStyles[$size]}

  &:hover:not(:disabled) {
    border-color: ${({ theme, $error }) =>
      $error ? theme.colors.border.error : theme.colors.border.focus};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme, $error }) =>
      $error ? theme.colors.border.error : theme.colors.border.focus};
    box-shadow: ${({ theme, $error }) =>
      $error ? theme.shadows.error : theme.shadows.focus};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.tertiary};
    opacity: 0.6;
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      background-color: ${({ theme }) => theme.colors.background.secondary};
      color: ${({ theme }) => theme.colors.text.tertiary};
      opacity: 0.6;
    `}
`

export const DropdownValue = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text.primary};

  &.placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

export const DropdownIcon = styled.span<{ $open: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: transform ${({ theme }) => theme.transitions.fast};

  ${({ $open }) =>
    $open &&
    css`
      transform: rotate(180deg);
    `}
`

export const DropdownContent = styled.div<StyledDropdownContentProps>`
  position: absolute;
  top: ${({ $position }) => ($position === 'top' ? 'auto' : '100%')};
  bottom: ${({ $position }) => ($position === 'top' ? '100%' : 'auto')};
  left: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  display: flex;
  flex-direction: column;
  min-width: ${({ $width }) => {
    if (typeof $width === 'number') return `${$width}px`
    if (typeof $width === 'string') return $width
    return '200px'
  }};
  max-height: ${({ $maxHeight }) => $maxHeight}px;
  margin-top: ${({ $position, theme }) =>
    $position === 'top' ? `-${theme.spacing.micro}` : theme.spacing.micro};

  ${({ theme }) => theme.animations.popIn}
  animation: popIn 0.2s ease-out;
`

export const SearchContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.default};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  flex-shrink: 0;
`

export const SearchInput = styled.input`
  width: 100%;
  height: 32px;
  padding: 0 ${({ theme }) => theme.spacing.default};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background.primary};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

export const DropdownList = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.text.secondary};
  }
`

export const Section = styled.div<StyledSectionProps>`
  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  }
`

export const SectionHeader = styled.div<StyledSectionHeaderProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.default}
    ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: ${({ $collapsible }) => ($collapsible ? 'pointer' : 'default')};
  user-select: none;

  &:hover {
    background: ${({ theme, $collapsible }) =>
      $collapsible
        ? theme.colors.background.tertiary
        : theme.colors.background.secondary};
  }
`

export const DropdownSectionTitle = styled.span`
  flex: 1;
`

export const SectionCount = styled.span`
  margin-left: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const SectionToggle = styled.span<{ $collapsed: boolean }>`
  margin-left: ${({ theme }) => theme.spacing.small};
  transition: transform ${({ theme }) => theme.transitions.fast};

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      transform: rotate(-90deg);
    `}
`

export const SectionContent = styled.div<{ $collapsed: boolean }>`
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'block')};
`

export const Option = styled.div<StyledOptionProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.default};
  padding: ${({ theme, $hasDescription }) =>
    $hasDescription
      ? `${theme.spacing.default} ${theme.spacing.medium}`
      : `${theme.spacing.small} ${theme.spacing.medium}`};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  background: ${({ theme, $selected, $disabled }) => {
    if ($disabled) return 'transparent'
    if ($selected) return theme.colors.primaryLight
    return 'transparent'
  }};
  color: ${({ theme, $disabled, $selected }) => {
    if ($disabled) return theme.colors.text.tertiary
    if ($selected) return theme.colors.primary
    return theme.colors.text.primary
  }};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover:not([disabled]) {
    background: ${({ theme, $selected }) =>
      $selected
        ? theme.colors.primaryLight
        : theme.colors.background.secondary};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.6;
    `}
`

export const OptionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  font-size: 14px;
`

export const OptionContent = styled.div`
  flex: 1;
  min-width: 0;
`

export const OptionLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const OptionDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  margin-top: ${({ theme }) => theme.spacing.micro};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing.large};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
`

export const LoadingState = styled.div`
  padding: ${({ theme }) => theme.spacing.large};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
`

export const ClearButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`
